import axios from "axios";
import { withRetries, isTransientRequestError } from "../utils/networkRetry";

const envBaseUrl = process.env.REACT_APP_API_BASE_URL || "";
const isBrowser = typeof window !== "undefined";
const isLocalhostRuntime =
    isBrowser &&
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

// Guard local development from stale/remote .env values.
const resolvedBaseUrl =
    isLocalhostRuntime && envBaseUrl.includes("onrender.com")
        ? "http://localhost:5001"
        : envBaseUrl;

const api = axios.create({
    baseURL: resolvedBaseUrl,
    withCredentials: true  // This ensures cookies are sent with requests
});

const inFlightGetRequests = new Map();

const stableStringify = (value) => {
    if (value === null || typeof value !== "object") {
        return JSON.stringify(value);
    }
    if (Array.isArray(value)) {
        return `[${value.map((item) => stableStringify(item)).join(",")}]`;
    }
    const keys = Object.keys(value).sort();
    return `{${keys.map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(",")}}`;
};

const buildGetRequestKey = (url, config = {}) => {
    const base = config.baseURL || api.defaults.baseURL || "";
    const params = stableStringify(config.params || {});
    return `${String(base)}|${String(url)}|${params}`;
};

api.interceptors.request.use((config) => {
    const token = localStorage.getItem("token");
    if (token) {
        // Set both header formats to ensure compatibility
        config.headers["auth-token"] = token;
        config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
});

const originalGet = api.get.bind(api);

const getOnceWithRetry = (url, config = {}) => {
    const maxRetries = Number.isFinite(config?.maxRetries) ? config.maxRetries : 3;
    const skipRetry = Boolean(config?.skipRetry);
    const axiosConfig = { ...config };
    delete axiosConfig.maxRetries;
    delete axiosConfig.skipRetry;
    delete axiosConfig.skipDeduplication;
    return withRetries(
        () => originalGet(url, axiosConfig),
        {
            retries: maxRetries,
            shouldRetry: (err) => !skipRetry && isTransientRequestError(err)
        }
    );
};

api.get = (url, config = {}) => {
    if (config?.skipDeduplication) {
        return getOnceWithRetry(url, config);
    }

    const requestKey = buildGetRequestKey(url, config);
    const existing = inFlightGetRequests.get(requestKey);
    if (existing) {
        return existing;
    }

    const requestPromise = getOnceWithRetry(url, config).finally(() => {
        inFlightGetRequests.delete(requestKey);
    });

    inFlightGetRequests.set(requestKey, requestPromise);
    return requestPromise;
};

api.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error.response?.status;
        const errMsg = error.response?.data?.error || "";

        console.log("Axios interceptor caught an error:", errMsg);

        // Only redirect on 401 for authenticated requests — never for login/signup endpoints
        const requestUrl = error.config?.url || "";
        const isAuthEndpoint = ["/api/auth/login", "/api/auth/signup", "/api/auth/send-otp", "/api/auth/verify-otp", "/api/auth/forgotpass", "/api/auth/resetpass"].some(p => requestUrl.includes(p));

        if (
            !isAuthEndpoint &&
            status === 401 &&
            (errMsg.toLowerCase().includes("expired") || errMsg.toLowerCase().includes("invalid"))
        ) {
            console.warn("Token invalid or expired. Redirecting to login...");
            localStorage.clear();
            window.location.replace("/login");
        }

        return Promise.reject(error);
    }
);

export default api;
